import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { useAuth } from '../hooks/useAuth'
import { Button } from '../components/ui/Button'
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card'
import { Alert, AlertDescription } from '../components/ui/Alert'

export function AdminBootstrap() {
  const navigate = useNavigate()
  const { user } = useAuth()
  const [loading, setLoading] = useState(false)
  const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null)

  const handleBootstrap = async () => {
    if (!user) {
      setMessage({ type: 'error', text: 'You must be logged in to bootstrap admin access.' })
      return
    }

    setLoading(true)
    setMessage(null)

    try {
      const { data, error } = await supabase.rpc('bootstrap_first_admin', {
        target_email: user.email
      })

      if (error) {
        throw error
      }

      if (data.success) {
        setMessage({ type: 'success', text: data.message })
        setTimeout(() => {
          window.location.href = '/admin'
        }, 2000)
      } else {
        setMessage({ type: 'error', text: data.error })
      }
    } catch (error: any) {
      console.error('Bootstrap error:', error)
      setMessage({ type: 'error', text: error.message || 'Failed to bootstrap admin access' })
    } finally {
      setLoading(false)
    }
  }

  const handleManualCheck = async () => {
    setLoading(true)
    setMessage(null)

    try {
      const { data, error } = await supabase.rpc('get_admin_users_emails')

      if (error) {
        throw error
      }

      if (data && data.length > 0) {
        const adminList = data.map((admin: any) => admin.email).join(', ')
        setMessage({
          type: 'success',
          text: `Current admins: ${adminList}`
        })
      } else {
        setMessage({
          type: 'error',
          text: 'No admin users found in the database.'
        })
      }
    } catch (error: any) {
      console.error('Check error:', error)
      setMessage({ type: 'error', text: error.message || 'Failed to check admin users' })
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-throttle-navy-900 via-throttle-navy-500 to-throttle-navy-900 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full">
        <button
          onClick={() => navigate('/')}
          className="mb-4 flex items-center gap-2 text-white hover:text-throttle-cyan-400 transition-colors"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to Home
        </button>

        <Card>
          <CardHeader className="text-center">
            <CardTitle>Admin Bootstrap Utility</CardTitle>
            <p className="text-sm text-gray-600 mt-2">
              This page helps you set up the first admin user if none exists.
            </p>
          </CardHeader>
          <CardContent className="space-y-4">
            {message && (
              <Alert variant={message.type === 'error' ? 'destructive' : 'default'}>
                <AlertDescription>{message.text}</AlertDescription>
              </Alert>
            )}

            {user ? (
              <div className="space-y-4">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-700">
                    <strong>Logged in as:</strong> {user.email}
                  </p>
                </div>

                <div className="space-y-3">
                  <Button
                    onClick={handleBootstrap}
                    disabled={loading}
                    className="w-full"
                  >
                    {loading ? 'Processing...' : 'Make Me the First Admin'}
                  </Button>

                  <Button
                    onClick={handleManualCheck}
                    disabled={loading}
                    variant="outline"
                    className="w-full"
                  >
                    {loading ? 'Checking...' : 'Check Current Admin Users'}
                  </Button>
                </div>

                <div className="mt-6 p-4 bg-blue-50 rounded-lg">
                  <h3 className="text-sm font-semibold text-blue-900 mb-2">How this works:</h3>
                  <ul className="text-xs text-blue-800 space-y-1 list-disc list-inside">
                    <li>This will only work if NO admin users exist yet</li>
                    <li>Once an admin exists, use the Admin panel to add more admins</li>
                    <li>After bootstrapping, you'll be redirected to the Admin panel</li>
                  </ul>
                </div>
              </div>
            ) : (
              <div className="text-center py-8">
                <p className="text-gray-600 mb-4">Please log in first to bootstrap admin access.</p>
                <Button onClick={() => navigate('/login')}>
                  Go to Login
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
